/**
 * Centralized snake_case (Postgres row) <-> camelCase (domain type) mapping.
 *
 * This is the ONLY module allowed to translate between database rows and
 * domain types from `@/types`. Repositories must use these mappers; feature
 * code never sees row shapes. Column names here MUST match the SQL in
 * supabase/migrations/ — keep both in sync when a column changes.
 *
 * Conventions:
 * - `*Row` types describe the database row (snake_case, nullable columns).
 * - `*FromRow` converts a confirmed server row to the domain type.
 * - `*ToInsert` / `*ToPatch` convert domain writes to row payloads.
 * - Null/undefined: optional domain fields map to nullable columns; `null`
 *   in a row becomes `undefined` on the domain type.
 */
import type {
  AppNotification,
  AuditEvent,
  BreathingSession,
  CaregiverClientLink,
  DailyHealth,
  HealthItem,
  HealthItemType,
  HealthLog,
  JournalEntry,
  LinkStatus,
  MedicationIntakeEvent,
  MedicationOccurrence,
  MedicationSchedule,
  NewJournalEntry,
  NewMedicationSchedule,
  NewNote,
  NewPlace,
  NewPlannerItem,
  Note,
  NotificationType,
  OccurrenceStatus,
  Place,
  PlannerItem,
  PlannerItemSource,
  Profile,
  RecurrenceRule,
  RecurrenceType,
  Role,
  UserSettings,
} from '@/types'

// ---------------------------------------------------------------------------
// profiles
// ---------------------------------------------------------------------------

export type ProfileRow = {
  id: string
  display_name: string | null
  avatar_path: string | null
  role: Role
  locale: string
  timezone: string
  created_at: string
  updated_at: string
}

export function profileFromRow(row: ProfileRow): Profile {
  return {
    id: row.id,
    displayName: row.display_name,
    avatarPath: row.avatar_path,
    role: row.role,
    locale: row.locale,
    timezone: row.timezone,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }
}

/** Never maps `role` — role changes are impossible via ordinary updates. */
export function profileToPatch(
  patch: Partial<Pick<Profile, 'displayName' | 'avatarPath' | 'locale' | 'timezone'>>,
): Partial<Pick<ProfileRow, 'display_name' | 'avatar_path' | 'locale' | 'timezone'>> {
  const row: Partial<Pick<ProfileRow, 'display_name' | 'avatar_path' | 'locale' | 'timezone'>> = {}
  if (patch.displayName !== undefined) row.display_name = patch.displayName
  if (patch.avatarPath !== undefined) row.avatar_path = patch.avatarPath
  if (patch.locale !== undefined) row.locale = patch.locale
  if (patch.timezone !== undefined) row.timezone = patch.timezone
  return row
}

// ---------------------------------------------------------------------------
// user_settings
// ---------------------------------------------------------------------------

export type UserSettingsRow = {
  user_id: string
  haptics: boolean
  sounds: boolean
  breathing_vibration: boolean
  planner_haptics: boolean
  reduced_motion: boolean
  high_contrast: boolean
  text_size: 'small' | 'normal' | 'large'
  nav_collapsed: boolean
  dark_mode: boolean
  updated_at: string
}

export function userSettingsFromRow(row: UserSettingsRow): UserSettings {
  return {
    userId: row.user_id,
    haptics: row.haptics,
    sounds: row.sounds,
    breathingVibration: row.breathing_vibration,
    plannerHaptics: row.planner_haptics,
    reducedMotion: row.reduced_motion,
    highContrast: row.high_contrast,
    textSize: row.text_size,
    navCollapsed: row.nav_collapsed,
    darkMode: row.dark_mode,
    updatedAt: row.updated_at,
  }
}

export function userSettingsToRow(
  settings: Partial<Omit<UserSettings, 'userId' | 'updatedAt'>>,
): Partial<Omit<UserSettingsRow, 'user_id' | 'updated_at'>> {
  const row: Partial<Omit<UserSettingsRow, 'user_id' | 'updated_at'>> = {}
  if (settings.haptics !== undefined) row.haptics = settings.haptics
  if (settings.sounds !== undefined) row.sounds = settings.sounds
  if (settings.breathingVibration !== undefined) row.breathing_vibration = settings.breathingVibration
  if (settings.plannerHaptics !== undefined) row.planner_haptics = settings.plannerHaptics
  if (settings.reducedMotion !== undefined) row.reduced_motion = settings.reducedMotion
  if (settings.highContrast !== undefined) row.high_contrast = settings.highContrast
  if (settings.textSize !== undefined) row.text_size = settings.textSize
  if (settings.navCollapsed !== undefined) row.nav_collapsed = settings.navCollapsed
  if (settings.darkMode !== undefined) row.dark_mode = settings.darkMode
  return row
}

// ---------------------------------------------------------------------------
// planner_items
// ---------------------------------------------------------------------------

export type PlannerItemRow = {
  id: string
  user_id: string
  date: string
  start_minute: number
  duration_minutes: number
  activity_id: string
  activity_name: string
  category: string
  color: string
  icon_id: string
  notes: string | null
  source: PlannerItemSource
  source_entity_id: string | null
  created_at: string
  updated_at: string
}

export function plannerItemFromRow(row: PlannerItemRow): PlannerItem {
  return {
    id: row.id,
    userId: row.user_id,
    date: row.date,
    startMinute: row.start_minute,
    durationMinutes: row.duration_minutes,
    activityId: row.activity_id,
    activityName: row.activity_name,
    category: row.category,
    color: row.color,
    iconId: row.icon_id,
    notes: row.notes ?? undefined,
    source: row.source,
    sourceEntityId: row.source_entity_id ?? undefined,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }
}

export function plannerItemToInsert(
  item: NewPlannerItem,
  userId: string,
): Omit<PlannerItemRow, 'id' | 'created_at' | 'updated_at'> {
  return {
    user_id: userId,
    date: item.date,
    start_minute: item.startMinute,
    duration_minutes: item.durationMinutes,
    activity_id: item.activityId,
    activity_name: item.activityName,
    category: item.category,
    color: item.color,
    icon_id: item.iconId,
    notes: item.notes ?? null,
    source: item.source,
    source_entity_id: item.sourceEntityId ?? null,
  }
}

export function plannerItemToPatch(
  patch: Partial<
    Pick<PlannerItem, 'date' | 'startMinute' | 'durationMinutes' | 'notes' | 'activityName' | 'category' | 'color' | 'iconId'>
  >,
): Partial<Omit<PlannerItemRow, 'id' | 'user_id' | 'created_at' | 'updated_at'>> {
  const row: Partial<Omit<PlannerItemRow, 'id' | 'user_id' | 'created_at' | 'updated_at'>> = {}
  if (patch.date !== undefined) row.date = patch.date
  if (patch.startMinute !== undefined) row.start_minute = patch.startMinute
  if (patch.durationMinutes !== undefined) row.duration_minutes = patch.durationMinutes
  if (patch.notes !== undefined) row.notes = patch.notes
  if (patch.activityName !== undefined) row.activity_name = patch.activityName
  if (patch.category !== undefined) row.category = patch.category
  if (patch.color !== undefined) row.color = patch.color
  if (patch.iconId !== undefined) row.icon_id = patch.iconId
  return row
}

// ---------------------------------------------------------------------------
// notes
// ---------------------------------------------------------------------------

export type NoteRow = {
  id: string
  user_id: string
  title: string
  content: string
  category: string
  pinned: boolean
  urgent: boolean
  done: boolean
  archived: boolean
  created_at: string
  updated_at: string
}

export function noteFromRow(row: NoteRow): Note {
  return {
    id: row.id,
    userId: row.user_id,
    title: row.title,
    content: row.content,
    category: row.category,
    pinned: row.pinned,
    urgent: row.urgent,
    done: row.done,
    archived: row.archived,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }
}

export function noteToInsert(note: NewNote, userId: string): Omit<NoteRow, 'id' | 'created_at' | 'updated_at'> {
  return {
    user_id: userId,
    title: note.title,
    content: note.content,
    category: note.category,
    pinned: note.pinned,
    urgent: note.urgent,
    done: note.done,
    archived: note.archived,
  }
}

export function noteToPatch(patch: Partial<NewNote>): Partial<Omit<NoteRow, 'id' | 'user_id' | 'created_at' | 'updated_at'>> {
  const row: Partial<Omit<NoteRow, 'id' | 'user_id' | 'created_at' | 'updated_at'>> = {}
  if (patch.title !== undefined) row.title = patch.title
  if (patch.content !== undefined) row.content = patch.content
  if (patch.category !== undefined) row.category = patch.category
  if (patch.pinned !== undefined) row.pinned = patch.pinned
  if (patch.urgent !== undefined) row.urgent = patch.urgent
  if (patch.done !== undefined) row.done = patch.done
  if (patch.archived !== undefined) row.archived = patch.archived
  return row
}

// ---------------------------------------------------------------------------
// journal_entries
// ---------------------------------------------------------------------------

export type JournalEntryRow = {
  id: string
  user_id: string
  date: string
  mood: number
  energy: number
  stress: number
  went_well: string
  was_difficult: string
  remember_tomorrow: string
  freewriting: string
  tags: string[]
  created_at: string
  updated_at: string
}

export function journalEntryFromRow(row: JournalEntryRow): JournalEntry {
  return {
    id: row.id,
    userId: row.user_id,
    date: row.date,
    mood: row.mood,
    energy: row.energy,
    stress: row.stress,
    wentWell: row.went_well,
    wasDifficult: row.was_difficult,
    rememberTomorrow: row.remember_tomorrow,
    freewriting: row.freewriting,
    tags: row.tags ?? [],
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }
}

export function journalEntryToInsert(
  entry: NewJournalEntry,
  userId: string,
): Omit<JournalEntryRow, 'id' | 'created_at' | 'updated_at'> {
  return {
    user_id: userId,
    date: entry.date,
    mood: entry.mood,
    energy: entry.energy,
    stress: entry.stress,
    went_well: entry.wentWell,
    was_difficult: entry.wasDifficult,
    remember_tomorrow: entry.rememberTomorrow,
    freewriting: entry.freewriting,
    tags: entry.tags,
  }
}

// ---------------------------------------------------------------------------
// health_items / health_logs / daily_health
// ---------------------------------------------------------------------------

export type HealthItemRow = {
  id: string
  user_id: string
  name: string
  type: HealthItemType
  dosage: string
  schedule: string
  created_at: string
  updated_at: string
}

export function healthItemFromRow(row: HealthItemRow): HealthItem {
  return {
    id: row.id,
    userId: row.user_id,
    name: row.name,
    type: row.type,
    dosage: row.dosage,
    schedule: row.schedule,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }
}

export function healthItemToInsert(
  input: Pick<HealthItem, 'name' | 'type' | 'dosage' | 'schedule'>,
  userId: string,
): Omit<HealthItemRow, 'id' | 'created_at' | 'updated_at'> {
  return {
    user_id: userId,
    name: input.name,
    type: input.type,
    dosage: input.dosage,
    schedule: input.schedule,
  }
}

export type HealthLogRow = {
  id: string
  user_id: string
  item_id: string
  date: string
  taken: boolean
  time: string | null
  created_at: string
}

export function healthLogFromRow(row: HealthLogRow): HealthLog {
  return {
    id: row.id,
    userId: row.user_id,
    itemId: row.item_id,
    date: row.date,
    taken: row.taken,
    time: row.time ?? undefined,
    createdAt: row.created_at,
  }
}

export type DailyHealthRow = {
  id: string
  user_id: string
  date: string
  hydration: number
  sleep_hours: number
  sleep_quality: number
  notes: string
  created_at: string
  updated_at: string
}

export function dailyHealthFromRow(row: DailyHealthRow): DailyHealth {
  return {
    id: row.id,
    userId: row.user_id,
    date: row.date,
    hydration: row.hydration,
    sleepHours: row.sleep_hours,
    sleepQuality: row.sleep_quality,
    notes: row.notes,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }
}

// ---------------------------------------------------------------------------
// places
// ---------------------------------------------------------------------------

export type PlaceRow = {
  id: string
  user_id: string
  category: string
  object_label: string
  subzone: string | null
  exact_location: string | null
  notes: string | null
  image_path: string | null
  created_at: string
  updated_at: string
}

export function placeFromRow(row: PlaceRow): Place {
  return {
    id: row.id,
    userId: row.user_id,
    category: row.category,
    objectLabel: row.object_label,
    subzone: row.subzone ?? undefined,
    exactLocation: row.exact_location ?? undefined,
    notes: row.notes ?? undefined,
    imagePath: row.image_path ?? undefined,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }
}

export function placeToInsert(place: NewPlace, userId: string): Omit<PlaceRow, 'id' | 'created_at' | 'updated_at'> {
  return {
    user_id: userId,
    category: place.category,
    object_label: place.objectLabel,
    subzone: place.subzone ?? null,
    exact_location: place.exactLocation ?? null,
    notes: place.notes ?? null,
    image_path: place.imagePath ?? null,
  }
}

export function placeToPatch(patch: Partial<NewPlace>): Partial<Omit<PlaceRow, 'id' | 'user_id' | 'created_at' | 'updated_at'>> {
  const row: Partial<Omit<PlaceRow, 'id' | 'user_id' | 'created_at' | 'updated_at'>> = {}
  if (patch.category !== undefined) row.category = patch.category
  if (patch.objectLabel !== undefined) row.object_label = patch.objectLabel
  if (patch.subzone !== undefined) row.subzone = patch.subzone ?? null
  if (patch.exactLocation !== undefined) row.exact_location = patch.exactLocation ?? null
  if (patch.notes !== undefined) row.notes = patch.notes ?? null
  if (patch.imagePath !== undefined) row.image_path = patch.imagePath ?? null
  return row
}

// ---------------------------------------------------------------------------
// breathing_sessions
// ---------------------------------------------------------------------------

export type BreathingSessionRow = {
  id: string
  user_id: string
  exercise_id: string
  duration_seconds: number
  completed: boolean
  interrupted: boolean
  started_at: string
  finished_at: string | null
  created_at: string
}

export function breathingSessionFromRow(row: BreathingSessionRow): BreathingSession {
  return {
    id: row.id,
    userId: row.user_id,
    exerciseId: row.exercise_id,
    durationSeconds: row.duration_seconds,
    completed: row.completed,
    interrupted: row.interrupted,
    startedAt: row.started_at,
    finishedAt: row.finished_at,
    createdAt: row.created_at,
  }
}

export function breathingSessionToInsert(
  input: Pick<BreathingSession, 'exerciseId' | 'durationSeconds' | 'completed' | 'interrupted' | 'startedAt' | 'finishedAt'>,
  userId: string,
): Omit<BreathingSessionRow, 'id' | 'created_at'> {
  return {
    user_id: userId,
    exercise_id: input.exerciseId,
    duration_seconds: input.durationSeconds,
    completed: input.completed,
    interrupted: input.interrupted,
    started_at: input.startedAt,
    finished_at: input.finishedAt,
  }
}

// ---------------------------------------------------------------------------
// caregiver_client_links
// ---------------------------------------------------------------------------

export type CaregiverClientLinkRow = {
  id: string
  caregiver_id: string
  client_id: string
  status: LinkStatus
  invited_by: string
  created_at: string
  accepted_at: string | null
  revoked_at: string | null
}

export function caregiverClientLinkFromRow(row: CaregiverClientLinkRow): CaregiverClientLink {
  return {
    id: row.id,
    caregiverId: row.caregiver_id,
    clientId: row.client_id,
    status: row.status,
    invitedBy: row.invited_by,
    createdAt: row.created_at,
    acceptedAt: row.accepted_at,
    revokedAt: row.revoked_at,
  }
}

// ---------------------------------------------------------------------------
// medication_schedules / occurrences / intake events
// ---------------------------------------------------------------------------

export type MedicationScheduleRow = {
  id: string
  client_id: string
  created_by: string
  name: string
  health_type: HealthItemType
  dosage: string
  quantity: number
  instructions: string | null
  start_date: string
  end_date: string | null
  local_time: string
  timezone: string
  recurrence_type: RecurrenceType
  recurrence_rule: RecurrenceRule
  active: boolean
  created_at: string
  updated_at: string
}

export function medicationScheduleFromRow(row: MedicationScheduleRow): MedicationSchedule {
  return {
    id: row.id,
    clientId: row.client_id,
    createdBy: row.created_by,
    name: row.name,
    healthType: row.health_type,
    dosage: row.dosage,
    quantity: row.quantity,
    instructions: row.instructions,
    startDate: row.start_date,
    endDate: row.end_date,
    localTime: row.local_time,
    timezone: row.timezone,
    recurrenceType: row.recurrence_type,
    recurrenceRule: row.recurrence_rule,
    active: row.active,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }
}

export function medicationScheduleToInsert(
  input: NewMedicationSchedule,
  createdBy: string,
): Omit<MedicationScheduleRow, 'id' | 'created_at' | 'updated_at'> {
  return {
    client_id: input.clientId,
    // Identity always comes from the session, never from caller input.
    created_by: createdBy,
    name: input.name,
    health_type: input.healthType,
    dosage: input.dosage,
    quantity: input.quantity,
    instructions: input.instructions,
    start_date: input.startDate,
    end_date: input.endDate,
    local_time: input.localTime,
    timezone: input.timezone,
    recurrence_type: input.recurrenceType,
    recurrence_rule: input.recurrenceRule,
    active: input.active,
  }
}

export function medicationScheduleToPatch(
  patch: Partial<NewMedicationSchedule>,
): Partial<Omit<MedicationScheduleRow, 'id' | 'created_at' | 'updated_at'>> {
  const row: Partial<Omit<MedicationScheduleRow, 'id' | 'created_at' | 'updated_at'>> = {}
  // clientId/createdBy are deliberately excluded: they are not reassignable.
  if (patch.name !== undefined) row.name = patch.name
  if (patch.healthType !== undefined) row.health_type = patch.healthType
  if (patch.dosage !== undefined) row.dosage = patch.dosage
  if (patch.quantity !== undefined) row.quantity = patch.quantity
  if (patch.instructions !== undefined) row.instructions = patch.instructions
  if (patch.startDate !== undefined) row.start_date = patch.startDate
  if (patch.endDate !== undefined) row.end_date = patch.endDate
  if (patch.localTime !== undefined) row.local_time = patch.localTime
  if (patch.timezone !== undefined) row.timezone = patch.timezone
  if (patch.recurrenceType !== undefined) row.recurrence_type = patch.recurrenceType
  if (patch.recurrenceRule !== undefined) row.recurrence_rule = patch.recurrenceRule
  if (patch.active !== undefined) row.active = patch.active
  return row
}

export type MedicationOccurrenceRow = {
  id: string
  schedule_id: string
  client_id: string
  scheduled_for: string
  local_date: string
  local_time: string
  status: OccurrenceStatus
  created_at: string
  updated_at: string
}

export function medicationOccurrenceFromRow(row: MedicationOccurrenceRow): MedicationOccurrence {
  return {
    id: row.id,
    scheduleId: row.schedule_id,
    clientId: row.client_id,
    scheduledFor: row.scheduled_for,
    localDate: row.local_date,
    localTime: row.local_time,
    status: row.status,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }
}

export type MedicationIntakeEventRow = {
  id: string
  occurrence_id: string
  client_id: string
  taken_at: string | null
  status: OccurrenceStatus
  proof_object_path: string | null
  proof_uploaded_at: string | null
  caregiver_viewed_at: string | null
  client_note: string | null
  created_at: string
  updated_at: string
}

export function medicationIntakeEventFromRow(row: MedicationIntakeEventRow): MedicationIntakeEvent {
  return {
    id: row.id,
    occurrenceId: row.occurrence_id,
    clientId: row.client_id,
    takenAt: row.taken_at,
    status: row.status,
    proofObjectPath: row.proof_object_path,
    proofUploadedAt: row.proof_uploaded_at,
    caregiverViewedAt: row.caregiver_viewed_at,
    clientNote: row.client_note,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }
}

// ---------------------------------------------------------------------------
// notifications / audit_events
// ---------------------------------------------------------------------------

export type AppNotificationRow = {
  id: string
  recipient_id: string
  actor_id: string | null
  type: NotificationType
  entity_type: string | null
  entity_id: string | null
  title: string
  body: string
  deep_link: string | null
  read_at: string | null
  created_at: string
}

export function appNotificationFromRow(row: AppNotificationRow): AppNotification {
  return {
    id: row.id,
    recipientId: row.recipient_id,
    actorId: row.actor_id,
    type: row.type,
    entityType: row.entity_type,
    entityId: row.entity_id,
    title: row.title,
    body: row.body,
    deepLink: row.deep_link,
    readAt: row.read_at,
    createdAt: row.created_at,
  }
}

export type AuditEventRow = {
  id: string
  actor_id: string
  subject_user_id: string | null
  action: string
  entity_type: string
  entity_id: string | null
  safe_metadata: Record<string, unknown>
  created_at: string
}

export function auditEventFromRow(row: AuditEventRow): AuditEvent {
  return {
    id: row.id,
    actorId: row.actor_id,
    subjectUserId: row.subject_user_id,
    action: row.action,
    entityType: row.entity_type,
    entityId: row.entity_id,
    safeMetadata: row.safe_metadata ?? {},
    createdAt: row.created_at,
  }
}
